import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, Circle } from "lucide-react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useWorkspaceAddons } from "@/hooks/useWorkspaceAddons";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import {
  downgradeTargets,
  downgradeReadiness,
  TIER_DISPLAY_NAMES,
  TIER_PRICING,
  type Tier,
} from "@/lib/tier";
import { toast } from "sonner";

type Billing = "monthly" | "yearly";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const DowngradeDialog = ({ open, onOpenChange }: Props) => {
  const { workspaces, allOwnedWorkspaces, refresh: refreshWorkspaces } = useWorkspace();
  const { user } = useAuth();
  const { subscription, refresh: refreshSub } = useSubscription();
  const { addons } = useWorkspaceAddons();
  const [target, setTarget] = useState<Tier | null>(null);
  const [billing, setBilling] = useState<Billing>("monthly");
  const [submitting, setSubmitting] = useState(false);
  const [activeMemberCount, setActiveMemberCount] = useState(0);

  const currentTier = (workspaces.find((w) => w.is_primary)?.account_tier ?? null) as Tier | null;
  const targets = downgradeTargets(currentTier);

  // Count add-on seats that are still actively billing (not yet flagged to
  // cancel at period end). Those are the ones the user must cancel first
  // if the target tier doesn't permit add-ons.
  const activeAddons = useMemo(
    () => addons.filter((a) =>
      ["active", "trialing", "past_due"].includes(a.status) && !a.cancel_at_period_end,
    ).length,
    [addons],
  );

  // Count team members + pending invites across all owned workspaces.
  // The downgrade gate blocks if the target tier can't seat them all.
  useEffect(() => {
    if (!open || !user) return;
    const ownedIds = (allOwnedWorkspaces ?? []).filter((w) => w.owner_user_id === user.id).map((w) => w.id);
    if (ownedIds.length === 0) { setActiveMemberCount(0); return; }
    let cancelled = false;
    (async () => {
      const [{ data: m }, { data: i }] = await Promise.all([
        supabase.from("workspace_members").select("id").in("workspace_id", ownedIds).neq("status", "revoked"),
        supabase.from("workspace_member_invites").select("id").in("workspace_id", ownedIds).eq("status", "pending"),
      ]);
      if (!cancelled) setActiveMemberCount((m?.length ?? 0) + (i?.length ?? 0));
    })();
    return () => { cancelled = true; };
  }, [open, user, allOwnedWorkspaces]);

  const readiness = useMemo(() => {
    if (!target) return null;
    return downgradeReadiness(target, {
      workspaceCount: workspaces.length,
      activeAddons,
      activeMemberCount,
    });
  }, [target, workspaces.length, activeAddons, activeMemberCount]);

  const submit = async () => {
    if (!target || !readiness?.ready) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("initiate-downgrade", {
        body: {
          targetTier: target,
          targetBilling: billing,
          environment: getStripeEnvironment(),
        },
      });
      const errPayload = (data as { error?: string } | null)?.error;
      if (error || errPayload) throw new Error(errPayload || error?.message);
      toast.success(`Downgrade to ${TIER_DISPLAY_NAMES[target]} scheduled.`);
      await Promise.all([refreshSub(), refreshWorkspaces()]);
      onOpenChange(false);
      setTarget(null);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString()
    : "the end of your billing period";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change plan</DialogTitle>
          <DialogDescription>
            Your new plan takes effect on {periodEnd}. Until then you keep full access.
          </DialogDescription>
        </DialogHeader>

        {!targets.length ? (
          <p className="text-sm text-muted-foreground">No lower tiers available from your current plan.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-sm">New plan</Label>
              <RadioGroup
                value={target ?? ""}
                onValueChange={(v) => setTarget(v as Tier)}
                className="mt-2 space-y-1"
              >
                {targets.map((t) => (
                  <div key={t} className="flex items-center gap-2">
                    <RadioGroupItem value={t} id={`tier-${t}`} />
                    <Label htmlFor={`tier-${t}`} className="font-normal cursor-pointer">
                      {TIER_DISPLAY_NAMES[t]} — ${TIER_PRICING[t as keyof typeof TIER_PRICING].monthly}/mo
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {target && (
              <div>
                <Label className="text-sm">Billing</Label>
                <RadioGroup value={billing} onValueChange={(v) => setBilling(v as Billing)} className="mt-2 flex gap-4">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="monthly" id="bill-m" />
                    <Label htmlFor="bill-m" className="font-normal cursor-pointer">Monthly</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="yearly" id="bill-y" />
                    <Label htmlFor="bill-y" className="font-normal cursor-pointer">Yearly</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {target && readiness && !readiness.ready && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-3">
                <p className="text-sm">
                  Before you can downgrade to <strong>{TIER_DISPLAY_NAMES[target]}</strong> (allows{" "}
                  {readiness.targetBaseLimit ?? "unlimited"} workspace
                  {readiness.targetBaseLimit === 1 ? "" : "s"}), you need to:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    {readiness.workspacesToRemove === 0 ? (
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    )}
                    <span>
                      Delete <strong>{readiness.workspacesToRemove}</strong> non-primary workspace
                      {readiness.workspacesToRemove === 1 ? "" : "s"}.{" "}
                      <span className="text-muted-foreground">
                        Open each workspace's tab in Settings → Danger zone to export and delete it.
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    {readiness.addonsToCancel === 0 ? (
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    )}
                    <span>
                      Cancel <strong>{readiness.addonsToCancel}</strong> add-on seat
                      {readiness.addonsToCancel === 1 ? "" : "s"}.{" "}
                      <span className="text-muted-foreground">
                        Use the "Manage Add-Ons" card on this page.
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    {readiness.membersToRemove === 0 ? (
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    )}
                    <span>
                      Revoke <strong>{readiness.membersToRemove}</strong> team member
                      {readiness.membersToRemove === 1 ? "" : "s"} or pending invite
                      {readiness.membersToRemove === 1 ? "" : "s"}.{" "}
                      <span className="text-muted-foreground">
                        Manage them under each workspace's Team members card.
                      </span>
                    </span>
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground">
                  We won't auto-delete anything. Once both items are checked, return here to schedule the downgrade.
                </p>
              </div>
            )}

            {target && readiness?.ready && (
              <p className="text-sm text-muted-foreground">
                You're already under the {TIER_DISPLAY_NAMES[target]} caps — nothing else to do.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={!target || !readiness?.ready || submitting}>
            {submitting ? "Scheduling…" : "Schedule downgrade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
